import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Mail, Phone, Building, Calendar, Plus, ArrowLeft, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [interactionData, setInteractionData] = useState({
    type: 'note',
    notes: ''
  });

  useEffect(() => {
    fetchCustomer();
    fetchInteractions();
  }, [id]);

  const fetchCustomer = async () => {
    try {
      const response = await axios.get(`${API}/customers/${id}`);
      setCustomer(response.data);
    } catch (error) {
      toast.error('Failed to load customer');
    } finally {
      setLoading(false);
    }
  };

  const fetchInteractions = async () => {
    try {
      const response = await axios.get(`${API}/interactions/customer/${id}`);
      setInteractions(response.data);
    } catch (error) {
      toast.error('Failed to load interactions');
    }
  };

  const handleAddInteraction = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/interactions`, {
        customer_id: id,
        ...interactionData
      });
      toast.success('Interaction added');
      setDialogOpen(false);
      setInteractionData({ type: 'note', notes: '' });
      fetchInteractions();
    } catch (error) {
      toast.error('Failed to add interaction');
    }
  };

  const handleDeleteCustomer = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/customers/${id}`);
      toast.success('Customer deleted successfully');
      navigate('/customers');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      <Navbar setSidebarOpen={setSidebarOpen} />
      
      <main className="md:ml-64 pt-16">
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="mb-4"
              data-testid="back-button"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Customers
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Customer Info */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="customer-info-card">
                <div className="flex items-center justify-center mb-6">
                  <div className="h-20 w-20 rounded-full bg-accent text-white flex items-center justify-center font-semibold text-2xl">
                    {customer?.name.charAt(0).toUpperCase()}
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-slate-900 text-center mb-6">{customer?.name}</h2>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-slate-500 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Email</p>
                      <p className="text-sm text-slate-900 break-all">{customer?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-slate-500 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Phone</p>
                      <p className="text-sm text-slate-900">{customer?.phone}</p>
                    </div>
                  </div>
                  {customer?.company && (
                    <div className="flex items-start gap-3">
                      <Building className="h-5 w-5 text-slate-500 mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Company</p>
                        <p className="text-sm text-slate-900">{customer.company}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-slate-500 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Created</p>
                      <p className="text-sm text-slate-900">{formatDate(customer?.created_at)}</p>
                    </div>
                  </div>
                </div>

                {customer?.notes && (
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <p className="text-xs text-slate-500 mb-2">Notes</p>
                    <p className="text-sm text-slate-700">{customer.notes}</p>
                  </div>
                )}

                {user?.role === 'admin' && (
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" className="w-full" disabled={deleting} data-testid="delete-customer-button">
                          <Trash2 className="h-4 w-4 mr-2" />
                          {deleting ? 'Deleting...' : 'Delete Customer'}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Customer</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete <strong>{customer?.name}</strong>? This will also delete all related interactions. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={handleDeleteCustomer} className="bg-red-600 hover:bg-red-700">
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>
            </div>

            {/* Interaction History */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="interactions-card">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-slate-900">Interaction History</h3>
                  <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" data-testid="add-interaction-button">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Interaction
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Interaction</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddInteraction} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="type">Type</Label>
                          <select
                            id="type"
                            className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                            value={interactionData.type}
                            onChange={(e) => setInteractionData({ ...interactionData, type: e.target.value })}
                            data-testid="interaction-type-select"
                          >
                            <option value="note">Note</option>
                            <option value="call">Call</option>
                            <option value="email">Email</option>
                            <option value="meeting">Meeting</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="notes">Notes *</Label>
                          <Textarea
                            id="notes"
                            value={interactionData.notes}
                            onChange={(e) => setInteractionData({ ...interactionData, notes: e.target.value })}
                            rows={4}
                            required
                            data-testid="interaction-notes-input"
                          />
                        </div>
                        <Button type="submit" className="w-full" data-testid="submit-interaction-button">
                          Add Interaction
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>

                <div className="space-y-4">
                  {interactions.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No interactions yet</p>
                  ) : (
                    interactions.map((interaction) => (
                      <div
                        key={interaction.id}
                        className="border border-slate-200 rounded-lg p-4"
                        data-testid={`interaction-${interaction.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent capitalize">
                            {interaction.type}
                          </span>
                          <span className="text-xs text-slate-500">{formatDate(interaction.created_at)}</span>
                        </div>
                        <p className="text-sm text-slate-700">{interaction.notes}</p>
                        {interaction.created_by_name && (
                          <p className="text-xs text-slate-500 mt-2">By {interaction.created_by_name}</p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};